import type { BotDifficulty } from '@cribbage/bots';
import { applyMove, createGame, teamOf, type GameState } from '@cribbage/engine';
import type { ActiveGame } from '@cribbage/protocol';
import { lastGameHref, type LastGame } from '~/store/lastGame.js';
import type { StartedGame } from '~/store/lastStarted.js';
import { seatFace, seatLooks, type FaceKind, type FaceLook, type SeatFace } from './faceOrder.js';
import type { LocalGameRecord } from '~/store/localGames.js';

/**
 * Picking a game back up: which one the home screen's Resume door offers,
 * where the door points, and what it says about the game behind it.
 *
 * Two kinds of game can be offered. A stored local game is `(config, seed,
 * moves)`, so its position is whatever the engine says after replaying the
 * moves; nothing about the score is kept beside them, and nothing here trusts
 * a copy. An online game's position is the room's, read live through Rails.
 */

/** Who the player is up against, for the door's second line. */
export type ResumeOpponents =
  | { readonly kind: 'bots'; readonly difficulty: BotDifficulty }
  | { readonly kind: 'pass' }
  | { readonly kind: 'online' };

/** One game the door can offer, local or online, drawn the same way. */
export interface ResumableGame {
  readonly kind: 'local' | 'online';
  /** The local record id, or the online game id. */
  readonly id: string;
  readonly href: string;
  readonly opponents: ResumeOpponents;
  readonly summary: ResumeSummary;
}

/**
 * The table's address for a stored game.
 *
 * Play setup's own address, built by `lastGameHref`, plus the game's id, so
 * the screen opens the game it was and the id carries on into the saves that
 * follow.
 */
export function resumeHref(record: LocalGameRecord): string {
  const { variant, playerCount: players, crib } = record.config;
  const launch: LastGame =
    record.kind === 'bot'
      ? { kind: 'bot', variant, players, difficulty: record.botDifficulty ?? 'medium', crib }
      : { kind: 'pass', variant, players, crib };
  return `${lastGameHref(launch)}&resume=${encodeURIComponent(record.id)}`;
}

/**
 * Replay a stored log to the position it stopped at.
 *
 * Null when the engine refuses a move. A log written by an older engine can
 * stop replaying after a rule change, and a game that cannot be rebuilt is not
 * one to offer.
 */
export function replayStored(record: LocalGameRecord): GameState | null {
  try {
    let state = createGame(record.config, record.seed).state;
    for (const { seat, move } of record.moves) state = applyMove(state, seat, move).state;
    return state;
  } catch {
    return null;
  }
}

/**
 * One side of the saved score: the head that stands for it, and its points.
 * The head's face (`SeatFace`) is the one its seat wears at the table.
 */
export interface ResumeSide extends SeatFace {
  readonly team: number;
  readonly score: number;
}

function resumeSide(
  team: number,
  kind: FaceKind,
  score: number,
  look: FaceLook | undefined,
  botLevel?: BotDifficulty | null,
): ResumeSide {
  return { team, ...seatFace({ kind, look, botLevel: botLevel ?? undefined }), score };
}

export interface ResumeSummary {
  readonly sides: readonly ResumeSide[];
  /** Minutes from the deal to the last move saved, at least one. */
  readonly minutes: number;
  readonly players: number;
}

/**
 * What the Resume door shows: each side's score with a head for it, the time
 * played, and the seat count. Null when the game cannot be resumed.
 *
 * One entry per scoring side, in team order, headed by the player's seat on
 * their own side and by the side's first seat on any other. Against bots the
 * record names the player's seat: seat 0, except at a three-player grid, where
 * the seats are drawn. Pass-and-play has no single "you".
 */
export function resumeSummary(record: LocalGameRecord, you?: FaceLook): ResumeSummary | null {
  const state = replayStored(record);
  // Unreplayable (null) or already over: either way, nothing to resume.
  if (state?.result !== null) return null;
  const seats = state.config.playerCount;
  const bots = record.kind === 'bot';
  const mine = bots ? (record.mySeat ?? 0) : null;
  const looks = seatLooks(
    Array.from({ length: seats }, (_, seat) => ({
      seat,
      isBot: bots && seat !== mine,
      isYou: seat === mine,
    })),
    you,
  );
  const sides: ResumeSide[] = [];
  for (let first = 0; first < seats; first += 1) {
    const team = teamOf(state.config, first);
    if (sides.some((side) => side.team === team)) continue;
    const seat = mine !== null && teamOf(state.config, mine) === team ? mine : first;
    const kind: FaceKind = !bots ? 'person' : seat === mine ? 'you' : 'bot';
    /* Every bot at a local table plays at the one level the record keeps. */
    sides.push(
      resumeSide(team, kind, state.scores[team] ?? 0, looks.get(seat), record.botDifficulty),
    );
  }
  const elapsed = Date.parse(record.finishedAt) - Date.parse(record.startedAt);
  const minutes = Number.isFinite(elapsed) ? Math.max(1, Math.round(elapsed / 60_000)) : 1;
  return { sides, minutes, players: seats };
}

/** A stored local game as the door offers it, or null when it cannot be resumed. */
export function localResumable(record: LocalGameRecord, you?: FaceLook): ResumableGame | null {
  const summary = resumeSummary(record, you);
  if (summary === null) return null;
  return {
    kind: 'local',
    id: record.id,
    href: resumeHref(record),
    opponents:
      record.kind === 'pass'
        ? { kind: 'pass' }
        : { kind: 'bots', difficulty: record.botDifficulty ?? 'medium' },
    summary,
  };
}

/**
 * A live online game as the door offers it.
 *
 * One side per team, the player's own first and the rest in team order, as
 * the table's own score line reads. The player's side is headed by their own
 * seat, the others by their first seat. The score is the room's, so it moves
 * as the game does. Minutes run from when the game was made, because the
 * table kept playing while the player was away.
 */
export function onlineResumable(
  active: ActiveGame,
  playerId: string | null,
  now: number,
  you?: FaceLook,
): ResumableGame {
  const { game, progress } = active;
  const mine = game.participations.find((seat) => seat.player_id === playerId)?.team;
  const teams = [...new Set(game.participations.map((seat) => seat.team))].sort((a, b) =>
    a === mine ? -1 : b === mine ? 1 : a - b,
  );
  const looks = seatLooks(
    game.participations.map((seat) => ({
      seat: seat.seat,
      isBot: seat.bot,
      isYou: seat.player_id === playerId && !seat.bot,
      avatarId: seat.avatar_id,
    })),
    you,
  );
  const sides: ResumeSide[] = teams.map((team) => {
    const onTeam = game.participations.filter((seat) => seat.team === team);
    const head = onTeam.find((seat) => seat.player_id === playerId) ?? onTeam[0];
    const kind: FaceKind =
      head?.player_id === playerId ? 'you' : head?.bot === true ? 'bot' : 'person';
    return resumeSide(
      team,
      kind,
      progress.scores[team] ?? 0,
      head === undefined ? undefined : looks.get(head.seat),
      head?.bot_difficulty,
    );
  });
  const elapsed = now - Date.parse(game.created_at);
  return {
    kind: 'online',
    id: game.id,
    href: `/game/${game.id}`,
    opponents: { kind: 'online' },
    summary: {
      sides,
      minutes: Number.isFinite(elapsed) ? Math.max(1, Math.round(elapsed / 60_000)) : 1,
      players: game.participations.length,
    },
  };
}

/**
 * Which of the two candidates the door offers: the newest game started or
 * joined, local or online (Mitch, 2026-09-30).
 *
 * `marker` is this device's record of the last game started or joined. An
 * online game that began before it, and is not the game it names, was left for
 * a newer one and is not offered. An online game newer than the marker was
 * joined on another device, and it wins over the local game. With no marker at
 * all, the newer of the two by start time wins.
 *
 * The local candidate arrives already chosen by the marker: the game it names,
 * or the newest unfinished game when there is no marker.
 */
export function chooseResumable({
  marker,
  local,
  localStartedAt,
  online,
  onlineStartedAt,
}: {
  readonly marker: StartedGame | null;
  readonly local: ResumableGame | null;
  readonly localStartedAt: string | null;
  readonly online: ResumableGame | null;
  readonly onlineStartedAt: string | null;
}): ResumableGame | null {
  const onlineStart = onlineStartedAt === null ? Number.NaN : Date.parse(onlineStartedAt);
  const liveOnline =
    online !== null &&
    (marker === null ||
      (marker.kind === 'online' && marker.id === online.id) ||
      !(Date.parse(marker.at) > onlineStart))
      ? online
      : null;
  if (liveOnline === null) return local;
  if (local === null) return liveOnline;
  if (marker !== null) return liveOnline;
  const localStart = localStartedAt === null ? Number.NaN : Date.parse(localStartedAt);
  return localStart > onlineStart ? local : liveOnline;
}
